import React, { useEffect, useState, useRef } from "react";
import {
  complete_kitting_details,
  get_qr_material_details,
} from "../../../utils/api";
import { toast } from "react-toastify";
import { Pagination, Popconfirm, Row, Col, Button, Tooltip, Empty } from "antd";
import DataTable from '@/components/DataTable';
import { QrcodeOutlined } from "@ant-design/icons";
import ReprintQr from '@/components/third-party/ReprintQr';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import { useMbmrAlias } from '@/hooks/useAppSetting';

// Icon alias for compatibility
const QrCode2Icon = QrcodeOutlined;
function CompletedKitting({ searchvalue, selectedRecipe, refresh, onColumnMenu, filterContent, filterActiveCount, onFilterClear }) {
  const formRef = useRef();
  const mbmrAlias = useMbmrAlias();
  const [current_expand, set_current_expand] = useState("");
  const [kittingLogs, setKittingLogs] = useState([]);
  const [total, set_total] = useState("");
  const [page_number, set_page_number] = useState(1);
  const [show_qr, set_show_qr] = useState(false);
  const [qr_image, set_qr_image] = useState("");
  const componentRef = useRef();
  const [selected_record, set_selected_record] = useState();
  const addKeysInFilterDataForCollapse = (filterData) => {
    const updatedFilterList = filterData?.map((item, index) => {
      return {
        ...item,
        key: index.toString(), // Update the key to the index converted to a string
      };
    });
    return updatedFilterList;
  };

  const allColumns = [
    {
      dataIndex: "batch_number",
      title: "Batch Number",
      key: "batch_number",
      align: "center",
      width: "32%",
      defaultVisible: true,
      sortable: true,
      sortType: "string",
      render: (text) => (
        <Tooltip title={text} placement="top">
          <div className="truncate w-full">{text}</div>
        </Tooltip>
      ),
    },
    {
      dataIndex: "scheduled_date",
      title: "Schedule Time",
      key: "scheduled_date",
      align: "center",
      width: "32%",
      defaultVisible: true,
      sortable: true,
      sortType: "date",
      render: (text) => {
        const display = text ? String(text).replace("T", " ").slice(0, 16) : "-";
        return (
          <Tooltip title={display} placement="top">
            <div className="truncate w-full">{display}</div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: "recipe_name",
      title: mbmrAlias,
      key: "recipe_name",
      align: "center",
      width: "36%",
      defaultVisible: true,
      sortable: true,
      sortType: "string",
      render: (text) => (
        <Tooltip title={text} placement="top">
          <div className="truncate w-full">{text || "-"}</div>
        </Tooltip>
      ),
    },
  ];

  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: "completed_kitting_config",
    columns: allColumns,
    data: kittingLogs,
    searchValue: searchvalue,
  });

  useEffect(() => {
    if (onColumnMenu) onColumnMenu(columnConfigMenu);
  }, [columnConfigMenu]);

  const expandedRowRender = (expandRecord) => {
    const updateData = expandRecord?.materials?.map((item, index) => {
      return {
        ...item,
        key: index.toString(), // Update the key to the index converted to a string
      };
    });

    const materials = [
      {
        title: "Material Name",
        dataIndex: "material_name",
        key: "material_name",
        align: "center",
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        align: "center",
        render: (text, record) => <div>{`${text} ${record?.unit}`}</div>,
      },
      {
        title: "Reprint QR",
        children: [
          {
            title: "Mother Bag",
            dataIndex: "motherBagQuantity",
            key: "motherBagQuantity",
            align: "center",
            render: (text, record) => {
              return (
                <Tooltip title="Print Mother Bag QR">
                  <Button
                    type="text"
                    icon={<QrCode2Icon style={{ color: "#1890ff", fontSize: 20 }} />}
                    onClick={() => {
                      get_qr_materials({
                        ...record,
                        qr_type: "M",
                        batch_number: expandRecord?.batch_number,
                      });
                      set_selected_record({
                        batch_number: expandRecord?.batch_number,
                        material_id: record?.material_id,
                        operation_id: record?.operation_id,
                        qr_type: "M",
                      });
                    }}
                  />
                </Tooltip>
              );
            },
          },
          {
            title: "Child Bag",
            dataIndex: "childBagQuantity",
            key: "childBagQuantity",
            align: "center",
            render: (text, record) => {
              return (
                <Tooltip title="Print Child Bag QR">
                  <Button
                    type="text"
                    icon={<QrCode2Icon style={{ color: "#1890ff", fontSize: 20 }} />}
                    onClick={() => {
                      get_qr_materials({
                        ...record,
                        qr_type: "C",
                        batch_number: expandRecord?.batch_number,
                      });
                      set_selected_record({
                        batch_number: expandRecord?.batch_number,
                        material_id: record?.material_id,
                        operation_id: record?.operation_id,
                        qr_type: "C",
                      });
                    }}
                  />
                </Tooltip>
              );
            },
          },
        ],
      },
    ];

    return (
      <DataTable
        columns={materials}
        data={updateData}
        pagination={false}
        height={null}
        bordered
      />
    );
  };
  const handleExpand = (expanded, record) => {
    if (expanded) {
      set_current_expand(record?.key);
    } else {
      set_current_expand("");
    }
  };

  //####################### Handle Api's######################################################

  const completed_kitting_logs = async () => {
    try {
      await complete_kitting_details(
        {
          page_number: page_number,
          ...(selectedRecipe && { recipe_id: selectedRecipe }),
        },
        success_completed_kitting
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const success_completed_kitting = (res) => {
    if (res?.data && res?.data?.data) {
      setKittingLogs(addKeysInFilterDataForCollapse(res?.data?.data));
      set_total(res?.data?.obj_count);
    } else {
      setKittingLogs([]);
    }
  };

  const get_qr_materials = async (req) => {
    const req_data = {
      batch_number: req.batch_number,
      operation_id: req.operation_id,
      material_id: req.material_id,
      qr_type: req.qr_type,
    };
    try {
      await get_qr_material_details(req_data, success_qr_details);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const success_qr_details = (res) => {
    if (res?.data && res?.data?.data?.length > 0) {
      set_qr_image(res?.data?.data[0]);
      set_show_qr(true);
    } else {
      set_qr_image([]);
      toast.warning("No Data Found");
    }
  };

  useEffect(() => {
    completed_kitting_logs();
  }, [page_number, refresh]);

  return (
    <Row gutter={[8, 8]}>
      <Col span={24}>
        <DataTable
            className="scheduled-batches-table"
            tableKey="completed_kitting_table"
            searchable
            columnConfig
            filterPanel
            filterContent={filterContent}
            filterActiveCount={filterActiveCount}
            onFilterClear={onFilterClear}
            showCount={false}
            data={kittingLogs || []}
            columns={allColumns}
            bordered
            pagination={{ pageSize: 10 }}
            // Responsive: was 260px flat, anchored to a 900px reference viewport so it scales on larger/smaller screens
            height="calc(100vh - 584px)"
            tableLayout="fixed"
            locale={{ emptyText: <Empty description="No completed kitting records found" /> }}
            expandable={{
              expandedRowRender,
              onExpand: handleExpand,
              expandedRowKeys: [current_expand],
            }}
          />
          {/* scroll.y only sets a max-height, not a floor — this min-height keeps the
              table's footprint fixed regardless of row count, matching every other
              tab's table on the parent /batches page (same .scheduled-batches-table
              class is defined once there). */}
          <style>
            {`
              .scheduled-batches-table .ant-table-body {
                min-height: calc(100vh - 640px);
              }
              .scheduled-batches-table .ant-table-placeholder {
                height: calc(100vh - 640px);
              }
              .scheduled-batches-table .ant-table-placeholder > td {
                height: calc(100vh - 640px) !important;
                vertical-align: middle;
                border-bottom: none !important;
              }
            `}
          </style>
      </Col>
      <Col span={24}>
        <ReprintQr
          set_show_qr={set_show_qr}
          show_qr={show_qr}
          set_qr_image={set_qr_image}
          qr_image={qr_image}
          componentRef={componentRef}
          selected_record={selected_record}
        />
      </Col>
    </Row>
  );
}

export default CompletedKitting;
